/**
 * The abuse console — `/v1/admin/abuse/*` (docs/04-backend/46 §4.4).
 *
 * **Two refusals live behind these five operations and they are not the same thing.** A rate
 * window clears itself; a block does not. The server reports them separately and so does this
 * module, because an agent who cannot tell them apart resets the counters, tells the devotee to
 * try again, and sends them straight back into the same wall.
 *
 * **A phone prefix and an IP range are one surface.** They are the same operator act — this source
 * may not reach us, because, until — and the server discriminates on `type` rather than splitting
 * them into two endpoints. Two screens for one decision would be two places to look when somebody
 * asks why a caller is refused.
 *
 * **Every mutation carries a reason and the server requires it.** That is the compensating control
 * for putting this on `support` rather than `superadmin`: the people who meet a wrongly-blocked
 * devotee are the support desk, and an unblock that needs a founder is an unblock that waits until
 * Monday.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authedClient } from '../../auth/session';
import { keys } from '../queryClient';
import type { components } from '../../../api/generated/schema';

export type BlockRow = components['schemas']['BlockRow'];
export type CounterRow = components['schemas']['CounterRow'];
export type PrincipalResponse = components['schemas']['PrincipalResponse'];
type BlockListResponse = components['schemas']['BlockListResponse'];
type ResetResponse = components['schemas']['ResetResponse'];

/** The server's two block kinds. It names them in its own refusal, so we do not invent labels. */
export const BLOCK_TYPES = ['phone_prefix', 'ip_range'] as const;
export type BlockType = (typeof BLOCK_TYPES)[number];

/**
 * The three principal shapes a rate-limit key can carry.
 *
 * `phone` is here because the OTP service counts on the number itself, which no bearer token ever
 * names — and it is the one an agent reaches for most, because "I can't get my code" is the
 * commonest reason anybody opens this screen at all.
 */
export const PRINCIPAL_TYPES = ['phone', 'ip', 'user'] as const;
export type PrincipalType = (typeof PRINCIPAL_TYPES)[number];

export interface BlocklistQuery extends Record<string, unknown> {
  page: number;
  page_size: number;
  type?: BlockType;
}

/** Exported for its own test: an absent filter and an explicit one are different requests. */
export function blocklistQueryString(params: BlocklistQuery): string {
  const search = new URLSearchParams({
    page: String(params.page),
    page_size: String(params.page_size),
  });
  // Only when it has a value: an empty `type=` is a filter on the empty string to a server, and
  // this one answers 422 for a type it does not know.
  if (params.type !== undefined) search.set('type', params.type);
  return search.toString();
}

export function useBlocklist(params: BlocklistQuery) {
  return useQuery({
    queryKey: keys.abuse.blocklist(params),
    queryFn: () =>
      authedClient.request<BlockListResponse>(
        `/v1/admin/abuse/blocklist?${blocklistQueryString(params)}`,
      ),
  });
}

/**
 * Every abuse write invalidates every abuse read.
 *
 * Coarse on purpose: adding a block changes the list AND what a principal lookup would say about
 * anybody inside it, and a hand-maintained map of which keys each write touches goes stale the
 * first time somebody adds a field.
 */
function useAbuseMutation<TArgs, TData>(fn: (args: TArgs) => Promise<TData>) {
  const client = useQueryClient();
  return useMutation<TData, unknown, TArgs>({
    mutationFn: fn,
    onSuccess: () => client.invalidateQueries({ queryKey: keys.abuse.all() }),
  });
}

export function useAddBlock() {
  return useAbuseMutation(
    (body: { type: BlockType; value: string; reason: string; expires_at?: string | null }) =>
      authedClient.request<BlockRow>('/v1/admin/abuse/blocklist', { method: 'POST', body }),
  );
}

/**
 * Lifting takes a reason too, and the server requires it.
 *
 * Lifting a block is as much a judgement as making one: the next person to see this range needs to
 * know somebody already decided it was clean.
 */
export function useRemoveBlock() {
  return useAbuseMutation(({ id, type, reason }: { id: number; type: BlockType; reason: string }) =>
    authedClient.request<void>(
      `/v1/admin/abuse/blocklist/${id}?type=${type}&reason=${encodeURIComponent(reason)}`,
      { method: 'DELETE' },
    ),
  );
}

export function usePrincipal(type: PrincipalType, value: string) {
  return useQuery({
    queryKey: keys.abuse.principal(type, value),
    queryFn: () =>
      authedClient.request<PrincipalResponse>(
        `/v1/admin/abuse/principals/${type}/${encodeURIComponent(value)}`,
      ),
    // Nothing is looked up until somebody asks. An empty box is not a query.
    enabled: value.trim().length > 0,
  });
}

/**
 * The support unblock. Clears the rate windows and NOTHING else.
 *
 * ⛔ It returns what it CLEARED, not what it was asked to clear — so the screen reports the
 * server's list rather than saying "done". An agent told "done" when there was nothing there
 * would close a ticket about somebody who is still locked out for a reason this never touched.
 */
export function useResetPrincipal() {
  return useAbuseMutation(
    ({ type, value, reason }: { type: PrincipalType; value: string; reason: string }) =>
      authedClient.request<ResetResponse>(
        `/v1/admin/abuse/principals/${type}/${encodeURIComponent(value)}/reset` +
          `?reason=${encodeURIComponent(reason)}`,
        { method: 'POST' },
      ),
  );
}
